import React, { useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDto } from 'api/Clients/Types/Client'
import { ComplexTransfer } from 'api/Transaction/Types/ComplexTransfer'
import { TransactionDirectionType } from 'api/Transaction/Types/TransactionDirectionType'
import { TransactionTypeDto } from 'api/Wallet/Types/TransactionTypeDto'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import MoreButton from 'components/MoreButton/MoreButton'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

export interface Transaction {
  id: number
  client: string
  wallet: WalletDto
  amount: number
  directionTypeId: string
}

export interface MultiAdjustmentFormValues {
  client: string | null
  fromWalletId: string
  amount: number
  comment: string
  directionTypeId: string
  transactionTypeId: number
  currency: string | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | null
}

function dataToSubmit(
  values: MultiAdjustmentFormValues,
  transactions: Transaction[]
): FormSubmitValues<ComplexTransfer> {
  return {
    transactionTypeId: values.transactionTypeId,
    transactions: transactions.map((transaction) => ({
      walletId: transaction.wallet.id,
      amount: transaction.amount,
      directionTypeId: transaction.directionTypeId,
    })),
    comment: values.comment,
  }
}

const MultiAdjustmentFormUI: React.FC<FormikProps<MultiAdjustmentFormValues> & OuterProps> = (
  props
) => {
  const { client, handleSubmit, isSubmitting, values, errors } = props
  const transactionTypeOptions = props.transactionTypes.map((transactionType) => ({
    id: transactionType.id,
    name: transactionType.name ?? '',
  }))

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form onSubmit={handleSubmit}>
      <DataGrid columns={1}>
        <DataColumn>
          <DataCard title='Transaction Details' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              rightAddon={
                <>
                  <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                    Select
                  </Button>
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='fromWalletId'
              label='Wallet'
              value={values.fromWalletId || undefined}
              options={props.wallets.map((wallet) => ({
                id: wallet.id,
                name: `${wallet.name}-balance:${wallet.balance}`,
              }))}
              error={errors.fromWalletId}
              onChange={(value) => {
                const nextWalletId = value ? String(value) : ''
                props.setFieldValue('fromWalletId', nextWalletId)
                props.setWallet(nextWalletId)
              }}
              rightAddon={
                values.fromWalletId && (
                  <Link
                    to={generatePath(links.recordsWallets.detailsUrl as string, {
                      id: values.fromWalletId,
                      slug: 'details',
                    })}
                    showIcon
                  />
                )
              }
            />
            <FormSelectField
              name='directionTypeId'
              label='Debit/Credit'
              value={values.directionTypeId || undefined}
              options={props.directionType.map((directionType) => ({
                id: directionType.id.toString(),
                name: directionType.name,
              }))}
              error={errors.directionTypeId}
              onChange={(value) => props.setFieldValue('directionTypeId', String(value))}
            />
            <FormField
              name='amount'
              label='Amount'
              type='number'
              error={errors.amount}
              rightAddon={
                props.currency ? (
                  <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                    {props.currency}
                  </span>
                ) : undefined
              }
            />
            <FormFieldLayout label='Actions'>
              <Button
                onClick={() =>
                  props.addTransaction({
                    directionTypeId: values.directionTypeId,
                    amount: values.amount,
                  })
                }
                disabled={isSubmitting || !values.fromWalletId || !values.amount}
                data-test='add-button'
                type='button'
              >
                Add
              </Button>
            </FormFieldLayout>
          </DataCard>

          <DataCard title='Source Transaction(s)'>
            <Grid
              resizable
              data={props.transactions.filter(
                (transaction) => transaction.directionTypeId === '100'
              )}
              style={{ margin: 0 }}
            >
              <Column field='client' title='Client' />
              <Column field='wallet.name' title='Wallet' />
              <Column field='amount' title='Amount' />
              <Column field='wallet.currency.id' title='Currency' />
              <Column
                width={50}
                title=''
                cell={(gridCellProps: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Delete',
                          onClick: () => props.removeTransaction(gridCellProps.dataItem.id),
                        },
                      ]}
                    />
                  </td>
                )}
              />
            </Grid>
          </DataCard>

          <DataCard title='Destination Transaction(s)'>
            <Grid
              resizable
              data={props.transactions.filter(
                (transaction) => transaction.directionTypeId === '200'
              )}
              style={{ margin: 0 }}
            >
              <Column field='client' title='Client' />
              <Column field='wallet.name' title='Wallet' />
              <Column field='amount' title='Amount' />
              <Column field='wallet.currency.id' title='Currency' />
              <Column
                width={50}
                title=''
                cell={(gridCellProps: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Delete',
                          onClick: () => props.removeTransaction(gridCellProps.dataItem.id),
                        },
                      ]}
                    />
                  </td>
                )}
              />
            </Grid>
          </DataCard>

          <DataCard title='Main Details'>
            <FormSelectField
              name='transactionTypeId'
              label='Transaction Type'
              value={values.transactionTypeId || undefined}
              options={transactionTypeOptions}
              error={errors.transactionTypeId}
              onChange={(value) => props.setFieldValue('transactionTypeId', Number(value))}
            />
            <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
          </DataCard>
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

interface OuterProps {
  client: ClientDto | null
  wallets: WalletDto[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  walletId: string
  directionType: TransactionDirectionType[]
  transactionTypes: TransactionTypeDto[]
  transactions: Transaction[]

  onSubmit(values: FormSubmitValues<ComplexTransfer>): Promise<void>
  setClient(): void
  setCurrency(currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string): void
  setWallet(walletId: string): void
  addTransaction(values: { directionTypeId: string; amount: number }): void
  removeTransaction(id: number): void
}

export const MultiAdjustmentForm = withFormik<OuterProps, MultiAdjustmentFormValues>({
  mapPropsToValues: ({ client, walletId, currency }) => ({
    client: client?.name || null,
    fromWalletId: walletId,
    currency,
    amount: 0,
    comment: '',
    directionTypeId: '100',
    transactionTypeId: 610,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.transactions))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<MultiAdjustmentFormValues> = {}

    if (!values.directionTypeId) {
      errors.directionTypeId = 'Required'
    }

    if (!values.transactionTypeId) {
      errors.transactionTypeId = 'Required'
    }

    if (!values.fromWalletId) {
      errors.fromWalletId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    return errors
  },
  enableReinitialize: true,
})(MultiAdjustmentFormUI)
